import React, { useEffect, useState } from 'react';
import { Typography } from '@material-ui/core';
import Chart from 'react-apexcharts';

import { useTranslation } from 'react-i18next';
import { useStyles } from './styles';
import { useWindowSize } from '../../../hooks/windowSize';

interface IDonutChart {
  title?: string;
  values: number[];
  categories?: string[];
  color: string;
  inactiveColor?: string;
  children?: React.ReactNode;
  margin?: number;
  width?: number | string;
  showLabel?: boolean;
  labelTitle?: string;
  labelSize?: number;
  unit?: string;
  tooltip?: boolean;
}

const CustomDonutChart: React.FC<IDonutChart> = ({
  title,
  values,
  categories,
  color,
  inactiveColor,
  children,
  margin,
  width,
  showLabel,
  labelTitle,
  labelSize,
  unit,
  tooltip,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [data, setData] = useState<number[]>([0, 0]);
  const [loading, setLoading] = useState(true);
  const size = useWindowSize();
  const [chartWidth, setChartWidth] = useState<string>('100%');

  useEffect(() => {
    if (values.length === 1 || (values[0] === 0 && values[1] === 0)) {
      setData([values[0], 100 - values[0]]);
      setLoading(false);
    } else {
      setData(values);
      setLoading(false);
    }
  }, [values]);

  useEffect(() => {
    if (size.width) {
      if (size.width < 1280) {
        setChartWidth('100%');
      } else if (size.width >= 1280 && size.width <= 1400) {
        setChartWidth('80%');
      } else if (size.width > 1400 && size.width <= 1600) {
        setChartWidth('70%');
      } else if (size.width > 1600) {
        setChartWidth('60%');
      }
    } else {
      setChartWidth('100%');
    }
  }, [size]);

  if (loading) {
    <div>
      <p>{t('components:feedback.loading')}</p>
    </div>;
  }

  return (
    <div className={classes.root} style={{ marginBottom: margin || 0 }}>
      {title && (
        <Typography className={classes.title} variant="h3">
          {title}
        </Typography>
      )}
      <Chart
        options={{
          chart: {
            type: 'donut',
            redrawOnParentResize: true,
            redrawOnWindowResize: true,
            sparkline: {
              enabled: false,
            },
            toolbar: {
              show: false,
            },
          },
          dataLabels: {
            enabled: false,
          },
          stroke: {
            show: false,
          },
          plotOptions: {
            pie: {
              expandOnClick: false,
              donut: {
                size: '80%',
                labels: {
                  show: true,
                  name: {
                    show: showLabel || false,
                    offsetY: -32,
                    fontFamily: 'Poppins',
                  },
                  total: {
                    show: true,
                    label:
                      labelTitle || t('components:charts.total').toUpperCase(),
                    fontFamily: 'Poppins',
                    fontWeight: 'bold',
                    color: '#7B7B7B',
                    showAlways: true,
                    formatter: w => {
                      const sum = w.globals.seriesTotals.reduce(
                        (a: number, b: number) => a + b,
                        0,
                      );
                      if (
                        values.length === 1 ||
                        (values[0] === 0 && values[1] === 0)
                      ) {
                        return unit ? `${values[0]}${unit}` : `${values[0]}`;
                      }
                      return unit ? `${sum}${unit}` : `${sum}`;
                    },
                  },
                  value: {
                    show: true,
                    fontFamily: 'Poppins',
                    fontSize: labelSize ? `${labelSize}px` : '24px',
                    fontWeight: 'bold',
                    formatter(val) {
                      return unit ? `${val}${unit}` : `${val}`;
                    },
                  },
                },
              },
            },
          },
          xaxis: {
            categories: categories || [
              t('components:charts.interval'),
              t('components:charts.other'),
            ],
            axisBorder: {
              show: false,
            },
            axisTicks: {
              show: false,
            },
            labels: {
              show: false,
            },
          },
          yaxis: {
            axisBorder: {
              show: false,
            },
            labels: {
              align: 'center',
            },
          },
          tooltip: {
            enabled: tooltip,
            custom: ({ series, seriesIndex, w }) => {
              return (
                '<div>' +
                '<span>' +
                `${w.globals.labels[seriesIndex]}: ${series[seriesIndex]}` +
                '</span>' +
                `</div>`
              );
            },
          },
          colors:
            values[0] === 0 && values.length === 1
              ? ['#CFD8DC']
              : [color, inactiveColor || '#CFD8DC'],
          legend: {
            show: false,
          },
        }}
        series={data}
        type="donut"
        width={chartWidth}
      />
      {children}
    </div>
  );
};

export default CustomDonutChart;
